import { mat2d, vec2 } from "gl-matrix";
import { Big } from "big.js";

import { updateToolMatrix } from "./Transformer/tool";
import { gatherProps } from "./Merge/MergeUtils.mjs";

import AppConstants from "../constants/AppConstants";
import {
  GRAPHIC_STYLE,
  GRAPHIC_STYLE_HORIZONTAL_SHIFT,
  GRAPHIC_STYLE_OFFSET_X,
  GRAPHIC_STYLE_OFFSET_Y,
  GRAPHIC_STYLE_SCALE_PERCENTAGE,
  GRAPHIC_STYLE_TYPE,
} from "../constants/MergeConstants";

const { GRAPHIC_SCALE_ZOOM } = AppConstants;
const RESULT_PRECISION = 3;

/**
 * * NOTES
 * * images in scale mode are always bound by their width
 */

/**
 * @template T
 * @typedef {import('immutable').Map<keyof T, T[keyof T]>} ImmutableMapFrom
 */

/**
 * @typedef {import("@/types/ElementTypes.mjs").DimensionPosition} DimensionPosition
 */

/**
 * @typedef {{
 *  x: number,
 *  y: number,
 *  w: number,
 *  h: number,
 *  a: number,
 *  gs: {
 *   [GRAPHIC_STYLE_TYPE]: string,
 *   [GRAPHIC_STYLE_SCALE_PERCENTAGE]?: number,
 *   [GRAPHIC_STYLE_OFFSET_X]?: number,
 *   [GRAPHIC_STYLE_OFFSET_Y]?: number
 *  }
 * }} Dimensions
 *
 * @typedef {ImmutableMapFrom<Dimensions>} ImmutableDimensions
 */

/**
 * @typedef {{
 *  backgroundScale: number,
 *  horizontalOffset: number,
 *  verticalOffset: number
 * }} ImageScales
 */

/**
 * Formats numbers down to three decimal places at max.
 * @param {Big} bigNumber
 * @returns {number}
 */

const formatPrecision = (bigNumber) => {
  let stringNumber = bigNumber.toFixed(RESULT_PRECISION);

  if (stringNumber.includes(".")) {
    while ((stringNumber.endsWith("0") || stringNumber.endsWith(".")) && stringNumber.length > 1) {
      const endsDecimal = stringNumber.endsWith(".");

      stringNumber = stringNumber.slice(0, stringNumber.length - 1);

      if (endsDecimal) break;
    }
  }

  return Number(stringNumber);
};

/**
 * Prepares scale and offsets from a dimensions map.
 * @param {ImmutableDimensions} dimensions
 * @returns {ImageScales}
 */

const getScalesFromDimensions = (dimensions) => ({
  backgroundScale: dimensions.getIn(GRAPHIC_STYLE_SCALE_PERCENTAGE.split("."), 100),
  horizontalOffset: dimensions.getIn(GRAPHIC_STYLE_OFFSET_X.split("."), 50),
  verticalOffset: dimensions.getIn(GRAPHIC_STYLE_OFFSET_Y.split("."), 50),
});

/**
 * Returns js dimension positions from immutable dimensions
 * @param {ImmutableDimensions} dimension
 * @returns {DimensionPosition}
 */
const getPositionsFromDimensions = (dimension) => {
  const { w, h, x, y, a } = dimension?.toJS();
  return { w, h, x, y, a };
};

/**
 *
 * @param {Pick<Dimensions, "x" | "y" |"w" |"h" | "a">} positions
 * @param {boolean=} withTranslation
 * @returns {mat2d}
 */
const dimensionsToMatrix = (positions, withTranslation = true) => {
  // use the matrix without translation to account for the offset created when an image is rotated.
  const matrix = updateToolMatrix({ ...positions });
  // re-zero to preserve scales/skew without shifting things transformed by it.
  if (!withTranslation) {
    matrix[4] = 0;
    matrix[5] = 0;
  }

  return matrix;
};

/**
 * Gets the original image size as { x,y,w,h,a } as a js object based on current and frame dimensions and aspect ratio.
 * @param {ImmutableDimensions} frameDimensions
 * @param {ImmutableDimensions} dimensions
 * @param {number} aspectRatio
 * @returns {DimensionPosition}
 */
export const calculateOriginalImageSize = (frameDimensions, dimensions, aspectRatio) => {
  const scales = getScalesFromDimensions(dimensions);
  const { w, h, x, y, a } = getPositionsFromDimensions(dimensions);

  // Convert percentages to decimals. Represents image focal point of it's final size
  const backgroundScale = scales.backgroundScale / 100;
  const horizontalOffset = scales.horizontalOffset / 100;
  const verticalOffset = scales.verticalOffset / 100;

  // scale % is dictated by the width, so the height is adjusted to the aspect ratio in this case
  const cropWidth = new Big(w).times(backgroundScale);
  const cropHeight = cropWidth.div(aspectRatio);

  // centers. Both are assumed to use the same top left at 0,0 initially.
  const frameCenter = [frameDimensions.get("w") / 2, frameDimensions.get("h") / 2];
  const imageCenter = [cropWidth.div(2).toNumber(), cropHeight.div(2).toNumber()];

  // offset to center image over the selection
  const [xDiff, yDiff] = vec2.sub(vec2.create(), imageCenter, frameCenter);

  // matches centers by shifting image x/y by center diff
  const centeredX = new Big(x).minus(xDiff);
  const centeredY = new Big(y).minus(yDiff);

  // inverse scales to use a portion of the diff to shift with offsets from center.
  const inverseHorizontal = new Big(horizontalOffset).minus(0.5).times(2);
  const inverseVertical = new Big(verticalOffset).minus(0.5).times(2);

  // scaled portion of the diff that can be used
  // left offset being 0 for example, would be -1 * the xDiff, effectively canceling out the shift to the center.
  // 1 (as in offset === 100) would effectively double it (causing the image to shift fully to the left)
  const xCornerOffset = new Big(xDiff).times(inverseHorizontal);
  const yCornerOffset = new Big(yDiff).times(inverseVertical);

  // use the matrix without translation to account for the offset created when an image is rotated.
  const frameMatrix = dimensionsToMatrix({ w, h, x, y, a }, false);

  // deltas are sent to unshifted delta space, effectively giving us the offset to account for when it's sent back ton the matrix.
  const [matXDelta, matYdelta] = vec2.transformMat2d(
    vec2.create(),
    [xCornerOffset, yCornerOffset],
    frameMatrix
  );

  const newX = centeredX.minus(matXDelta);
  const newY = centeredY.minus(matYdelta);

  return {
    x: newX.toNumber(),
    y: newY.toNumber(),
    w: cropWidth.toNumber(),
    h: cropHeight.toNumber(),
    a,
  };
};

/**
 * Maps frame and current crop dimensions back to data model graphic styles.
 * @param {ImmutableDimensions} frameDimensions 
 * @param {ImmutableDimensions} cropDimensions 
 * @param {number} aspectRatio 
 * @returns {ImageScales}
 */
export const mapCropToDataModel = (frameDimensions, cropDimensions, aspectRatio) => {
  const framePositions = getPositionsFromDimensions(frameDimensions);
  const cropPositions = getPositionsFromDimensions(cropDimensions);

  // * send both coordinates to the matrix because they have the same rotation, so their x/y will be relative.
  // todo: change this if we ever end up rotating the two independently.
  const frameMatrix = dimensionsToMatrix(framePositions);
  const cropMatrix = dimensionsToMatrix(cropPositions);

  // todo: consider removing eventually. Just commented out in case there are issues.
  // little offset when h/w matches to prevent scaling by 0
  // if (framePositions.w === cropPositions.w || framePositions.h === cropPositions.h) {
  //   const yOffset = new Big(cropPositions.h).times(OFFSET);
  //   const xOffset = new Big(yOffset).times(aspectRatio);

  //   cropPositions.w = new Big(cropPositions.w).plus(xOffset).toNumber();
  //   cropPositions.h = new Big(cropPositions.h).plus(yOffset).toNumber();

  //   cropPositions.x = new Big(cropPositions.x).minus(new Big(xOffset).div(2)).toNumber();
  //   cropPositions.y = new Big(cropPositions.y).minus(new Big(yOffset).div(2)).toNumber();
  // }

  const frameInverseRotationMatrix = mat2d.invert(
    mat2d.create(),
    dimensionsToMatrix(framePositions, false)
  );

  // diff in space relative to each matrix
  const diff = vec2.sub(
    vec2.create(),
    [cropMatrix[4], cropMatrix[5]],
    [frameMatrix[4], frameMatrix[5]]
  );

  // rotate back as the diff local to the frame
  const [localCropX, localCropY] = vec2.transformMat2d(
    vec2.create(),
    diff,
    frameInverseRotationMatrix
  );

  const frameCenter = [
    new Big(framePositions.w).div(2).toNumber(),
    new Big(framePositions.h).div(2).toNumber(),
  ];

  const cropCenter = [
    new Big(cropPositions.w).div(2).plus(localCropX).toNumber(),
    new Big(cropPositions.h).div(2).plus(localCropY).toNumber(),
  ];

  const centerDiff = vec2.sub(vec2.create(), frameCenter, cropCenter);

  // still the top left corner xy, but after the crop is centered to the frame. Also effectively a diff from the frame xy.
  const [centeredTLx, centeredTLy] = vec2.add(vec2.create(), [localCropX, localCropY], centerDiff);

  const offsetRatioX = centeredTLx
    ? new Big(localCropX).div(centeredTLx).minus(1).times(0.5).plus(0.5)
    : new Big(0.5);

  const offsetRatioY = centeredTLy
    ? new Big(localCropY).div(centeredTLy).minus(1).times(0.5).plus(0.5)
    : new Big(0.5);

  const newScale = new Big(cropPositions.w).div(framePositions.w).times(100);
  const newHorizontalOffset = offsetRatioX.times(100);
  const newVerticalOffset = offsetRatioY.times(100);

  return {
    backgroundScale: formatPrecision(newScale),
    horizontalOffset: formatPrecision(newHorizontalOffset),
    verticalOffset: formatPrecision(newVerticalOffset),
  };
};

/**
 * Converts to the equivalent of centered fill if not crop and a large enough zoom to fill
 * @param {Object} dimensions - The current element dimensions
 * @param {number} aspectRatio
 * @returns {ImmutableDimensions} - Converted dimensions with GRAPHIC_SCALE_ZOOM settings
 */
export const convertToZoomMode = (dimensions, aspectRatio) => {
  const currentMode = dimensions.getIn(GRAPHIC_STYLE_TYPE.split("."));
  const containerWidth = dimensions.get("w");
  const containerHeight = dimensions.get("h");
  const currentZoomPercentage = dimensions.getIn(GRAPHIC_STYLE_SCALE_PERCENTAGE.split("."));

  const containerAR = new Big(containerWidth).div(containerHeight);

  // Calculate original image dimensions based on aspect ratio
  let imageWidth = containerWidth;
  let imageHeight = containerHeight;

  const widthBound = containerAR.gt(aspectRatio);
  const heightBound = containerAR.lt(aspectRatio);

  // image wider
  if (heightBound) {
    imageHeight = containerHeight;
    imageWidth = imageHeight * aspectRatio;
  }
  // image taller
  if (widthBound) {
    imageWidth = containerWidth;
    imageHeight = imageWidth / aspectRatio;
  }

  // Get the current position from gs.s if it exists
  const fillOffset = new Big(dimensions.getIn(GRAPHIC_STYLE_HORIZONTAL_SHIFT.split("."), 50)).div(
    100
  );

  const horizontalPosition = (containerAR.lt(aspectRatio) ? fillOffset : new Big(0.5))
    .times(100)
    .toNumber();
  const verticalPosition = (containerAR.gt(aspectRatio) ? fillOffset : new Big(0.5))
    .times(100)
    .toNumber();

  let zoomPercentage = widthBound
    ? new Big(containerWidth).div(imageWidth)
    : new Big(containerHeight).div(new Big(containerWidth).div(aspectRatio));

  // add one since the image will always be larger than the frame when scaled
  if (zoomPercentage.lt(1)) zoomPercentage = zoomPercentage.plus(1);

  zoomPercentage = zoomPercentage.times(100).toNumber();

  // use current zoom if it at least fills the element frame
  if (currentMode === GRAPHIC_SCALE_ZOOM && currentZoomPercentage >= zoomPercentage) {
    return dimensions;
  }

  return dimensions.merge({
    [GRAPHIC_STYLE]: {
      t: GRAPHIC_SCALE_ZOOM,
      p: zoomPercentage,
      x: horizontalPosition,
      y: verticalPosition,
    },
  });
};

/**
 * Returns image dimensions.
 * @param {Object} element 
 * @param {import("./UpdateBuilder.mjs").LAYOUTS[number]} layout 
 * @returns {Dimensions | ImmutableDimensions}
 */
export const getDimensions = (element, layout) =>
  gatherProps(element, layout, [GRAPHIC_STYLE_TYPE, "x", "y", "w", "h", "a"], true);
